import {
  AfterViewInit,
  Component,
  HostBinding,
  Inject,
  OnInit,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SessionStorageService } from '@maximizer/core/shared/data-access';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import {
  CDN_URL,
  GlobalServicesLoginDetails,
  OAuthRedirectResult,
} from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  ON_PREM_CLIENT_ID,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import { LoginType, OUTLOOK_SHARED_UI } from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { catchError, finalize } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-login-email',
  templateUrl: './login-email.component.html',
  imports: [
    FormsModule,
    KENDO_INPUTS,
    KENDO_BUTTON,
    KENDO_DROPDOWNLIST,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class OutlookLoginEmailComponent implements OnInit, AfterViewInit {
  @HostBinding('class.max-outlook') hostClass = true;

  LoginType = LoginType;
  login: LoginType = LoginType.Cloud;
  cloudForm?: FormGroupWrapper<{ email: string }>;
  onPremForm?: FormGroupWrapper<{ hostServer: string }>;
  dbForm?: FormGroupWrapper<{ alias: string }>;
  globalServiceDatabases: GlobalServicesLoginDetails[] = [];
  dropdownDbs: { text: string; tenantId: string }[] = [];
  selectedDatabase?: { text: string; tenantId: string };
  currentStep = 1;
  loading = false;
  email = '';

  constructor(
    @Inject(CDN_URL) public readonly cdn: string,
    @Inject(ON_PREM_CLIENT_ID) private readonly onPremClientId: string,
    private readonly insights: InsightsService,
    private readonly sessionStorage: SessionStorageService,
    private readonly oauthService: OAuth2Service,
    private readonly activatedRoute: ActivatedRoute,
    readonly outlookStore: OutlookStore,
  ) {}

  ngOnInit(): void {
    this.insights.trackPageView();

    const urlParams = new URLSearchParams(globalThis.location.search);
    const type = urlParams.get('type');

    if (this.isValidLoginType(type)) {
      this.login = type;
    } else {
      this.closeLoginDialog();
      return;
    }

    if (this.login === LoginType.OnPrem) {
      this.onPremForm = FormWrapperBuilder.group<{ hostServer: string }>({
        hostServer: [
          '',
          [
            Validators.required,
            Validators.pattern(
              /^https?:\/\/([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/,
            ),
          ],
        ],
      });
    } else {
      this.setCloudForm();
      this.proceed();
    }
  }

  ngAfterViewInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      const email = params['email'];
      if (email) {
        this.email = email.toString();
        this.cloudForm?.group.get('email')?.setValue(email);
      }
    });
  }

  private isValidLoginType(type: unknown): type is LoginType {
    return type === LoginType.Cloud || type === LoginType.OnPrem;
  }

  private setCloudForm(): void {
    try {
      this.cloudForm = FormWrapperBuilder.group<{ email: string }>(
        {
          email: [
            this.email,
            [
              Validators.maxLength(255),
              Validators.pattern(
                /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
              ),
            ],
          ],
        },

        this.proceed,
        () => {
          this.cloudForm?.control.setErrors(null);
          if (!this.cloudForm?.value.email) {
            const email = this.cloudForm?.group.get('email');
            email?.setErrors({ required: true });
          }
        },
        { updateOn: 'submit' },
      );
    } catch (error) {
      this.logErrorToInsights('login-email-set-cloud-form-failed', error);
    }
  }

  private setDbForm(): void {
    try {
      this.dbForm = FormWrapperBuilder.group<{ alias: string }>(
        {
          alias: [null, [Validators.required]],
        },

        this.navigateToOAuth2,
        () => {
          this.dbForm?.control.setErrors(null);
        },
      );
    } catch (error) {
      this.logErrorToInsights('login-email-set-db-form-failed', error);
    }
  }

  proceed(fromUser = false): void {
    if (!this.cloudForm) {
      return;
    }
    if (fromUser) {
      this.cloudForm.group.markAllAsTouched();
    }
    const validation = this.cloudForm.validate();
    if (!validation.valid) return;

    const email = this.cloudForm?.group.get('email');

    if (!this.cloudForm?.value.email) {
      if (fromUser) email?.setErrors({ required: true });
      return;
    }

    this.getDatabases(this.cloudForm.value.email);
  }

  goBack(): void {
    if (this.currentStep === 1) {
      this.closeLoginDialog();
    } else {
      this.currentStep = 1;
      this.dbForm = undefined;
      this.dropdownDbs = [];
      this.globalServiceDatabases = [];
    }
  }

  getDatabases(email: string): void {
    this.email = email;
    this.loading = true;
    this.oauthService
      .getLoginDetails(email)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        catchError(async (error) => {
          const cloudForm = this.cloudForm?.group.get('email');
          cloudForm?.setErrors({ exist: true });
          this.logErrorToInsights('login-email-get-login-details-failed', error);
        }),
      )
      .subscribe((result) => {
        if (result == null || result?.length === 0) {
          const cloudForm = this.cloudForm?.group.get('email');
          cloudForm?.setErrors({ exist: true });
          return;
        }
        this.globalServiceDatabases = result;
        this.setDbForm();
        this.dropdownDbs =
          this.globalServiceDatabases.map((o) => ({
            text: o.tenant.name,
            tenantId: o.tenant.id,
          })) ?? [];

        this.selectedDatabase = { ...this.dropdownDbs[0] };

        if (result.length === 1) {
          this.navigateToOAuth2();
        } else {
          this.currentStep = 2;
          this.insights.trackPageView();
        }
      });
  }

  connect(): void {
    if (!this.onPremForm) return;

    const hostControl = this.onPremForm.controls.hostServer;
    const hostServer = hostControl.value?.trim() ?? '';

    const { valid } = this.onPremForm.validate();
    if (!valid || !hostServer) {
      hostControl.control.setErrors({ pattern: true });
      return;
    }

    this.loading = true;
    this.sessionStorage.set('on_prem_host', hostServer);

    this.isHostServerReachable(hostServer)
      .then((reachable) => {
        if (!reachable) {
          hostControl.control.setErrors({ notFound: true });
          return;
        }

        const success = this.navigateToOAuth2();
        if (!success) {
          this.logErrorToInsights(
            'login-email-oauth2-navigation-failed',
            new Error('Failed to navigate to OAuth2.'),
          );
        }
      })
      .catch(() => {
        hostControl.control.setErrors({ notFound: true });
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private async isHostServerReachable(hostServerUrl: string): Promise<boolean> {
    try {
      const response = await fetch(hostServerUrl, {
        method: 'HEAD',
        mode: 'no-cors',
      });

      // any response, including opaque, assume the server exists
      return response.ok || response.type === 'opaque';
    } catch {
      return false;
    }
  }

  navigateToOAuth2(): boolean {
    const url = globalThis.location.origin;
    const redirectUri = `${url}/authorize`;
    let final: OAuthRedirectResult | undefined;

    this.sessionStorage.set('url', url);
    this.sessionStorage.set('login_type', this.login);

    if (this.login === LoginType.Cloud) {
      final = this.handleCloudLogin(redirectUri);
    } else if (this.login === LoginType.OnPrem) {
      final = this.handleOnPremLogin(redirectUri);
    } else {
      return false;
    }

    if (!final) return false;

    this.sessionStorage.set('auth_code', JSON.stringify(final));
    this.loading = true;
    globalThis.location.href = final.url;
    return true;
  }

  private handleCloudLogin(
    redirectUri: string,
  ): OAuthRedirectResult | undefined {
    const selected = this.selectedDatabase;
    if (!selected) return;

    const loginDetails = this.globalServiceDatabases.find(
      (o) => o.tenant.id === selected.tenantId,
    );
    if (!loginDetails) return;

    const final = this.oauthService.getOAuthLoginUrl(loginDetails, redirectUri);
    if (!final) return;

    if (loginDetails.user_id) {
      final.url += `&user_id=${encodeURIComponent(loginDetails.user_id)}`;
    }

    this.sessionStorage.set('login_details', JSON.stringify(loginDetails));
    return final;
  }

  private handleOnPremLogin(
    redirectUri: string,
  ): OAuthRedirectResult | undefined {
    const host = this.onPremForm?.value.hostServer;
    if (!host) return;

    const result = this.oauthService.getOAuthLoginUrlOnPrem(
      host,
      redirectUri,
      this.onPremClientId,
    );

    return result ?? undefined;
  }

  private closeLoginDialog(): void {
    const messageObject = { messageType: 'dialogClosed' };
    const jsonMessage = JSON.stringify(messageObject);
    Office.context.ui.messageParent(jsonMessage);
  }

  private logErrorToInsights(eventName: string, error: unknown): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    this.insights.trackException({
      error: errorObj,
      properties: { component: 'LoginEmailComponent', eventName },
    });
  }
}
